/**
 * Cài đặt › Kết nối › API báo cáo (docs/specs/reporting-api.md §12). The registrar stays the system
 * of record and keeps running here; Twenty CRM and internal reports only read, through a separate
 * read-only address. This page lists the places that receive data, what each receives, its keys
 * (who created them, last use, expiry, state), how far each kind of data has been received, and
 * the steps to connect Twenty 2.44.
 *
 * Without `reporting_key.manage` the page is read-only: it shows, it never changes.
 */

import { ChevronDown, KeyRound, Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { api } from '../../../api';
import type { ReportingConsumerView, ReportingKey, ReportingSyncStatus, ReportingWarning } from '../../../api/areas/reporting';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout, DataTable, Section, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { EmptyState, Loading } from '../../../components/States';
import { useAccess } from '../../../lib/access';
import { formatCount, formatDate, formatDateTime, formatRelative } from '../../../lib/format';
import { ALL_DATASETS, REPORTING_SCOPES, keyState, suspendReason, syncProgress, type KeyState } from '../../../lib/reportingApi';
import { useApi } from '../../../lib/useApi';
import { AccessChangesPanel } from '../../operator/AccessChanges';
import { ActionDialog, type DialogBodyProps } from '../../operator/shared';
import { AddPlaceBody, IssueKeyForm, ReceiveForm, RenameForm, ResetForm, ResumeKeyForm, RevokeKeyForm, RotateKeyForm, useScopeLabels } from './dialogs';
import { TwentyGuide } from './TwentyGuide';
import { HistoryView, ParkedView, ReconcileView, SignalsView } from './views';

const MANAGE = ['reporting_key.manage'];

const KEY_TONE: Record<KeyState, Tone> = { ACTIVE: 'ok', EXPIRING: 'warn', SUSPENDED: 'crit', EXPOSED: 'crit', REVOKED: 'neutral', EXPIRED: 'neutral' };

type PlaceState = 'RECEIVING' | 'NOTHING' | 'NO_KEY' | 'DISABLED';
const PLACE_TONE: Record<PlaceState, Tone> = { RECEIVING: 'ok', NOTHING: 'neutral', NO_KEY: 'warn', DISABLED: 'neutral' };

export function ReportingApiPage() {
  const { t } = useTranslation('reporting');
  useDocumentTitle(t('title'));
  const access = useAccess();
  const canManage = access.has(MANAGE);
  const [tick, setTick] = useState(0);
  const list = useApi((signal) => api.listReportingConsumers({ signal }), [tick]);
  const reload = () => setTick((n) => n + 1);
  const production = list.status === 'success' && list.data.production;
  return (
    <>
      <PageHeader title={t('title')} subtitle={t('intro')} />
      <div className="space-y-6" data-reporting-page>
        {production ? <Callout tone="info">{t('production')}</Callout> : null}
        {canManage ? <AccessChangesPanel area="REPORTING" reloadKey={tick} onChanged={reload} hint={t('approval.pendingHint')} /> : null}
        <Section
          title={t('places.title')}
          description={t('places.description')}
          actions={
            canManage ? (
              <ActionDialog label={t('places.add')} icon={Plus} title={t('add.title')} description={t('add.description')} variant="default" wide onChanged={reload}>
                {(p) => <AddPlaceBody {...p} production={production} />}
              </ActionDialog>
            ) : null
          }
        >
          {list.status === 'loading' ? <Loading rows={3} /> : null}
          {list.status === 'error' ? <DataError error={list.error} onRetry={list.reload} title={t('loadFailed')} /> : null}
          {list.status === 'success' && list.data.items.length === 0 ? (
            <EmptyState title={t('places.empty')}>{t('places.emptyHint')}</EmptyState>
          ) : null}
          {list.status === 'success' ? (
            <div className="grid gap-4 [&>*]:min-w-0">
              {list.data.items.map((v) => (
                <PlaceCard key={v.consumer.id} v={v} canManage={canManage} production={production} onChanged={reload} />
              ))}
            </div>
          ) : null}
        </Section>
        <TwentyGuide />
      </div>
    </>
  );
}

function placeState(v: ReportingConsumerView): PlaceState {
  if (v.consumer.disabled_at) return 'DISABLED';
  if (v.consumer.datasets.length === 0) return 'NOTHING';
  if (!v.keys.some((k) => k.status === 'ACTIVE')) return 'NO_KEY';
  return 'RECEIVING';
}

/** One line per kind of warning: the kinds of data behind it are named together. */
function groupWarnings(list: readonly ReportingWarning[]): { code: ReportingWarning['code']; datasets: string[]; count: number }[] {
  const out: { code: ReportingWarning['code']; datasets: string[]; count: number }[] = [];
  for (const w of list) {
    // Rows set aside stay one line per kind of data: each has its own count.
    const same = w.code === 'PARKED_ROWS' ? undefined : out.find((x) => x.code === w.code);
    if (same) {
      if (w.dataset) same.datasets.push(w.dataset);
    } else out.push({ code: w.code, datasets: w.dataset ? [w.dataset] : [], count: w.count ?? 0 });
  }
  return out;
}

function WarningText({ w, name }: { w: ReturnType<typeof groupWarnings>[number]; name: string }) {
  const { t } = useTranslation('reporting');
  const labels = useScopeLabels();
  return <>{t(`warning.${w.code}`, { name, label: labels.datasets(sortDatasets(w.datasets)), count: w.count })}</>;
}

/** Datasets in the Control Center's order (the server sorts them by name). */
function sortDatasets(list: readonly string[]): string[] {
  return [...list].sort((a, b) => ALL_DATASETS.indexOf(a) - ALL_DATASETS.indexOf(b));
}

function SyncLine({ s }: { s: ReportingSyncStatus }) {
  const { t } = useTranslation('reporting');
  const labels = useScopeLabels();
  const label = labels.dataset(s.dataset);
  const p = syncProgress(s);
  const text =
    p.kind === 'never'
      ? t('place.sync.never', { label })
      : p.kind === 'catchingUp'
        ? p.minutes === null
          ? t('place.sync.catchingUpNoEta', { label, rows: formatCount(p.rows) })
          : t('place.sync.catchingUp', { label, rows: formatCount(p.rows), minutes: formatCount(p.minutes) })
        : p.kind === 'lagging'
          ? t('place.sync.lagging', { label, minutes: formatCount(p.minutes) })
          : t('place.sync.upToDate', { label });
  const tone = p.kind === 'never' || (p.kind === 'lagging' && p.minutes >= 60) ? 'text-warning' : 'text-foreground';
  return (
    <li className={tone} data-sync={s.dataset} title={s.last_acknowledged_at ? formatDateTime(s.last_acknowledged_at) : undefined}>
      {text}
    </li>
  );
}

type PlaceAction = 'receive' | 'key' | 'history' | 'reset' | 'parked' | 'reconcile' | 'signals' | 'rename';

function PlaceCard({ v, canManage, production, onChanged }: { v: ReportingConsumerView; canManage: boolean; production: boolean; onChanged: () => void }) {
  const { t } = useTranslation('reporting');
  const labels = useScopeLabels();
  const c = v.consumer;
  const state = placeState(v);
  const [open, setOpen] = useState<PlaceAction | null>(null);
  const [dirty, setDirty] = useState(false);
  const close = () => {
    setOpen(null);
    if (dirty) {
      setDirty(false);
      onChanged();
    }
  };
  const parked = v.checkpoints.reduce((n, s) => n + s.parked_rows, 0);
  const scopes = REPORTING_SCOPES.filter((s) => c.datasets.includes(s));
  const body = (a: PlaceAction, p: DialogBodyProps): ReactNode => {
    switch (a) {
      case 'receive':
        return <ReceiveForm consumer={c} {...p} />;
      case 'key':
        return <IssueKeyForm consumer={c} production={production} {...p} />;
      case 'history':
        return <HistoryView consumer={c} close={p.close} />;
      case 'reset':
        return <ResetForm consumer={c} {...p} />;
      case 'parked':
        return <ParkedView consumer={c} canManage={canManage} {...p} />;
      case 'reconcile':
        return <ReconcileView consumer={c} close={p.close} />;
      case 'signals':
        return <SignalsView consumer={c} signals={v.signals} canManage={canManage} {...p} />;
      case 'rename':
        return <RenameForm consumer={c} {...p} />;
    }
  };
  const title: Record<PlaceAction, string> = {
    receive: t('receive.title', { name: c.name }),
    key: t('key.title', { name: c.name }),
    history: t('history.title', { name: c.name }),
    reset: t('reset.title', { name: c.name }),
    parked: t('parked.title', { name: c.name }),
    reconcile: t('reconcile.title', { name: c.name }),
    signals: t('signals.title'),
    rename: t('rename.title', { name: c.name }),
  };
  const description: Partial<Record<PlaceAction, string>> = { receive: t('receive.description'), key: t('key.description') };
  const wide: Partial<Record<PlaceAction, boolean | 'xl'>> = { receive: true, history: 'xl', parked: true, reconcile: true, signals: true };
  const menu: { id: PlaceAction; label: string; manage: boolean }[] = [
    { id: 'parked', label: parked ? `${t('parked.action')} (${formatCount(parked)})` : t('parked.action'), manage: false },
    { id: 'reconcile', label: t('reconcile.action'), manage: false },
    { id: 'signals', label: t('signals.action'), manage: false },
    { id: 'reset', label: t('reset.action'), manage: true },
    { id: 'rename', label: t('rename.action'), manage: true },
  ];

  return (
    <article className="grid min-w-0 gap-4 rounded-xl border p-4 [&>*]:min-w-0" data-reporting-consumer={c.name}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold">{c.name}</h3>
            <Badge tone={PLACE_TONE[state]}>{t(`place.state.${state}`)}</Badge>
          </div>
          {c.description ? <p className="text-sm text-muted-foreground">{c.description}</p> : null}
          <p className="text-xs text-muted-foreground" title={formatDateTime(c.created_at)}>
            {c.created_by_name ? t('place.addedBy', { name: c.created_by_name, when: formatRelative(c.created_at) }) : t('place.added', { when: formatRelative(c.created_at) })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManage ? (
            <>
              <Button type="button" size="sm" variant="outline" onClick={() => setOpen('receive')} data-action="receive">
                {t('receive.action')}
              </Button>
              <Button type="button" size="sm" onClick={() => setOpen('key')} disabled={Boolean(c.disabled_at)} data-action="key">
                <KeyRound className="size-4" aria-hidden="true" />
                {t('key.create')}
              </Button>
            </>
          ) : null}
          <Button type="button" size="sm" variant="outline" onClick={() => setOpen('history')} data-action="history">
            {t('history.action')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" size="sm" variant="ghost" data-action="more">
                {t('more')} <ChevronDown className="size-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {menu
                .filter((m) => canManage || !m.manage)
                .map((m) => (
                  <DropdownMenuItem key={m.id} onSelect={() => setOpen(m.id)} data-menu={m.id}>
                    {m.label}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {v.warnings.length ? (
        <ul className="grid gap-2" data-warnings>
          {groupWarnings(v.warnings).map((w) => (
            <li key={`${w.code}/${w.datasets.join()}`}>
              <Callout tone={w.code === 'KEY_SUSPENDED' || w.code === 'NOT_FETCHED_2H' ? 'serious' : 'warn'} className="py-2">
                <WarningText w={w} name={c.name} />
              </Callout>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] [&>*]:min-w-0">
        <dl className="grid content-start gap-3 text-sm">
          <div className="grid gap-1">
            <dt className="text-xs font-medium text-muted-foreground">{t('place.receives')}</dt>
            <dd className="flex flex-wrap items-start gap-1" data-receives>
              {scopes.length ? (
                scopes.map((s) => (
                  <Badge key={s} tone="info" icon={false}>
                    {labels.scope(s)}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground">{t('place.nothing')}</span>
              )}
            </dd>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <div>
              <dt className="inline text-xs font-medium text-muted-foreground">{t('place.contacts')}: </dt>
              <dd className="inline font-medium" data-contacts={c.contacts ? 'on' : 'off'}>
                {c.contacts ? t('yes') : t('no')}
              </dd>
            </div>
            {c.weekly_resweep ? <div className="text-xs text-muted-foreground">{t('place.weekly')}</div> : null}
          </div>
        </dl>
        <div className="grid content-start gap-1 text-sm">
          <span className="text-xs font-medium text-muted-foreground">{t('place.syncTitle')}</span>
          {v.checkpoints.length ? (
            <ul className="grid gap-0.5 text-xs">
              {sortDatasets(v.checkpoints.map((s) => s.dataset)).map((d) => {
                const s = v.checkpoints.find((x) => x.dataset === d);
                return s ? <SyncLine key={d} s={s} /> : null;
              })}
            </ul>
          ) : (
            <span className="text-xs text-muted-foreground">{scopes.length ? t('place.syncNone') : '—'}</span>
          )}
        </div>
      </div>

      <KeysTable v={v} canManage={canManage} production={production} onChanged={onChanged} />

      <Dialog open={open !== null} onOpenChange={(o) => (o ? undefined : close())}>
        <DialogContent
          className={`max-h-[92vh] overflow-y-auto [&>*]:min-w-0 ${open && wide[open] === 'xl' ? 'sm:max-w-4xl' : open && wide[open] ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{open ? title[open] : ''}</DialogTitle>
            {open && description[open] ? <DialogDescription>{description[open]}</DialogDescription> : null}
          </DialogHeader>
          {open ? body(open, { close, changed: () => setDirty(true) }) : null}
        </DialogContent>
      </Dialog>
    </article>
  );
}

function KeysTable({ v, canManage, production, onChanged }: { v: ReportingConsumerView; canManage: boolean; production: boolean; onChanged: () => void }) {
  const { t } = useTranslation('reporting');
  const c = v.consumer;
  const columns: Column<ReportingKey>[] = [
    {
      key: 'key',
      header: t('keys.col.key'),
      className: 'whitespace-normal',
      cell: (k) => (
        <span className="block min-w-48">
          <span className="font-medium">{k.name ?? '—'}</span>
          <span className="block font-mono text-xs text-muted-foreground">{k.key_prefix}…</span>
          <span className="block text-xs">
            {t('keys.col.purpose')}: {k.purpose ? t(`purpose.${k.purpose}`) : '—'}
          </span>
          <span className="block text-xs text-muted-foreground">{k.allowed_cidrs.length ? `${t('keys.addresses', { count: k.allowed_cidrs.length })}: ${k.allowed_cidrs.join(', ')}` : t('keys.anyAddress')}</span>
        </span>
      ),
    },
    {
      key: 'createdBy',
      header: t('keys.col.createdBy'),
      cell: (k) => (
        <span title={formatDateTime(k.created_at)}>
          {k.created_by_name ?? '—'}
          <span className="block text-xs text-muted-foreground">{formatRelative(k.created_at)}</span>
        </span>
      ),
    },
    {
      key: 'lastUsed',
      header: t('keys.col.lastUsed'),
      cell: (k) =>
        k.last_used_at ? (
          <span title={formatDateTime(k.last_used_at)}>
            {formatRelative(k.last_used_at)}
            {k.last_ip ? <span className="block font-mono text-xs text-muted-foreground">{t('keys.from', { ip: k.last_ip })}</span> : null}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('keys.neverUsed')}</span>
        ),
    },
    {
      key: 'expires',
      header: t('keys.col.expires'),
      className: 'whitespace-nowrap',
      cell: (k) => (k.expires_at ? <span title={formatDateTime(k.expires_at)}>{formatDate(k.expires_at)}</span> : '—'),
    },
    {
      key: 'state',
      header: t('keys.col.state'),
      className: 'whitespace-normal',
      cell: (k) => {
        const s = keyState(k);
        return (
          <span className="flex flex-col items-start gap-1" data-key-state={s}>
            <Badge tone={KEY_TONE[s]}>{t(`keyState.${s}`)}</Badge>
            {s === 'SUSPENDED' || s === 'EXPOSED' ? <span className="text-xs text-muted-foreground">{t(`suspended.${suspendReason(k.suspended_reason)}`)}</span> : null}
            {s === 'REVOKED' && k.revoked_at ? <span className="text-xs text-muted-foreground">{t('keys.revokedOn', { when: formatRelative(k.revoked_at) })}</span> : null}
            {k.replaces_credential_id ? <span className="text-xs text-muted-foreground">{t('keys.replaces')}</span> : null}
          </span>
        );
      },
    },
  ];
  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      cell: (k) => <KeyActions consumer={v} keyRow={k} production={production} onChanged={onChanged} />,
    });
  }
  return (
    <div className="grid min-w-0 gap-2">
      <h4 className="text-sm font-semibold">{t('keys.title')}</h4>
      <div className="min-w-0 rounded-lg border" data-reporting-keys={c.name}>
        <DataTable columns={columns} rows={v.keys} rowKey={(k) => k.id} empty={<EmptyState title={t('keys.empty')} />} />
      </div>
    </div>
  );
}

function KeyActions({ consumer, keyRow, production, onChanged }: { consumer: ReportingConsumerView; keyRow: ReportingKey; production: boolean; onChanged: () => void }) {
  const { t } = useTranslation('reporting');
  const c = consumer.consumer;
  const s = keyState(keyRow);
  if (s === 'REVOKED' || s === 'EXPIRED') return null;
  const key = `${keyRow.key_prefix}…`;
  return (
    <div className="flex min-w-24 flex-col items-stretch gap-1.5" data-key-actions={keyRow.key_prefix}>
      {s === 'SUSPENDED' ? (
        <ActionDialog label={t('resume.action')} title={t('resume.title', { key })} onChanged={onChanged}>
          {(p) => <ResumeKeyForm keyRow={keyRow} {...p} />}
        </ActionDialog>
      ) : null}
      {s === 'ACTIVE' || s === 'EXPIRING' ? (
        <ActionDialog label={t('rotate.action')} title={t('rotate.title', { key })} description={t('rotate.description')} onChanged={onChanged}>
          {(p) => <RotateKeyForm consumer={c} keyRow={keyRow} production={production} {...p} />}
        </ActionDialog>
      ) : null}
      <ActionDialog label={t('revoke.action')} title={t('revoke.title', { key })} description={s === 'EXPOSED' ? t('resume.exposed') : undefined} variant="destructive" onChanged={onChanged}>
        {(p) => <RevokeKeyForm consumer={c} keyRow={keyRow} {...p} />}
      </ActionDialog>
    </div>
  );
}
